import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Save, X, FileText, Upload, Calendar, AlertTriangle } from 'lucide-react';
import { NoticeFormData } from '../../types';
import * as yup from 'yup';

interface NoticeFormProps {
  onSubmit: (data: NoticeFormData) => void;
  loading?: boolean;
  onCancel: () => void;
  initialData?: Partial<NoticeFormData>;
}

const noticeSchema = yup.object({
  title: yup
    .string()
    .required('Title is required')
    .min(5, 'Title must be at least 5 characters')
    .max(100, 'Title must not exceed 100 characters'),
  description: yup
    .string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters'),
  priority: yup
    .string()
    .required('Priority is required')
    .oneOf(['Low', 'Medium', 'High', 'Urgent'], 'Please select a valid priority'),
  date: yup
    .string()
    .required('Date is required'),
  published: yup
    .boolean()
    .required(),
  attachment: yup
    .mixed()
    .optional()
});

const NoticeForm: React.FC<NoticeFormProps> = ({
  onSubmit,
  loading = false,
  onCancel,
  initialData
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(noticeSchema),
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      priority: initialData?.priority || 'Medium',
      date: initialData?.date || new Date().toISOString().split('T')[0],
      published: initialData?.published ?? true,
    }
  });

  const priority = watch('priority');
  const published = watch('published');

  const handleFormSubmit = (data: any) => {
    const formData = {
      ...data,
      attachment: selectedFile || undefined
    };
    onSubmit(formData as NoticeFormData);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 'text-[#4ade80] bg-[#173322]';
      case 'Medium':
        return 'text-[#60a5fa] bg-[#16212b]';
      case 'High':
        return 'text-[#f0b64c] bg-[#332711]';
      case 'Urgent':
        return 'text-[#f38b83] bg-[#341a17]';
      default:
        return 'text-[#8a7f6e] bg-[#211c15]';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4">Notice Details</h3>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Title *
              </label>
              <input
                type="text"
                {...register('title')}
                placeholder="Enter notice title"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.title && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Description *
              </label>
              <textarea
                {...register('description')}
                rows={6}
                placeholder="Enter notice description..."
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent resize-vertical"
              />
              {errors.description && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Priority *
                </label>
                <select
                  {...register('priority')}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
                >
                  <option value="Low">Low Priority</option>
                  <option value="Medium">Medium Priority</option>
                  <option value="High">High Priority</option>
                  <option value="Urgent">Urgent</option>
                </select>
                {errors.priority && (
                  <p className="text-[#f38b83] text-sm mt-1">{errors.priority.message}</p>
                )}
                {priority && (
                  <div className="mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(priority)}`}>
                      {priority} Priority
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  <Calendar className="w-4 h-4 inline-block mr-1" />
                  Date *
                </label>
                <input
                  type="date"
                  {...register('date')}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
                />
                {errors.date && (
                  <p className="text-[#f38b83] text-sm mt-1">{errors.date.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* File Attachment */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4">Attachment</h3>

          <div className="mb-4">
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
              Attach File (Optional)
            </label>
            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-2 px-4 py-2 bg-[#211c15] hover:bg-[#28221a] border border-[#3a3122] rounded-lg cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                <span className="text-sm">Choose File</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  onChange={handleFileChange}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] hidden"
                />
              </label>
              <span className="text-sm text-[#8a7f6e]">
                PDF, DOC, DOCX, JPG, PNG, TXT (Max 10MB)
              </span>
            </div>
          </div>

          {selectedFile && (
            <div className="p-3 bg-[#28221a] border border-[#3a3122] rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="w-4 h-4 text-[#8a7f6e]" />
                  <div>
                    <div className="text-sm font-medium text-[#f3ede2]">{selectedFile.name}</div>
                    <div className="text-xs text-[#8a7f6e]">{formatFileSize(selectedFile.size)}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="p-1 text-[#f38b83] hover:bg-[#341a17] rounded transition-colors"
                  title="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Publishing Options */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4">Publishing</h3>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              {...register('published')}
              id="published"
              className="w-4 h-4 text-[#e2916d] border-[#3a3122] rounded focus:ring-[#e2916d]"
            />
            <label htmlFor="published" className="text-sm font-medium text-[#b9ad9b]">
              Publish immediately
            </label>
          </div>

          <div className="mt-2 p-3 bg-info-50 border border-info-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 text-info-600 mt-0.5" />
              <div className="text-sm text-info-700">
                {published
                  ? "This notice will be visible to all users immediately after saving."
                  : "This notice will be saved as draft and can be published later."
                }
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-[#3a3122]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
          >
            <X className="w-4 h-4 inline-block mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>{loading ? 'Saving...' : initialData ? 'Update' : 'Create'} Notice</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default NoticeForm;